'use client';

import FaIcon, { faClock, faTruck } from '@/components/icon/FaIcon/FaIcon';
import {
  estimatedShippingDateTextI18n,
  eventStatusLabel,
  hasShippingFee,
  showEstimatedShippingDate,
} from '@/features/lottery/utils';
import { getLocalizedText, isJapanese } from '@/lib/l18n/function';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { formatCurrency, formatDateCommonI18n } from '@/utils/formatters';
import { useTranslation } from 'react-i18next';
import type { LotteryBenefit, LotteryCustomOption, LotteryPrice } from '../../types';
import ShippingAlert from '../ShippingAlert/ShippingAlert';
import styles from './LotteryDetailHeader.module.scss';

function LotteryDetailHeader({
  name,
  name_en,
  startAt,
  endAt,
  status,
  benefit,
  isCampaign = false,
  showSpKv = false,
  estimatedShippingDate,
  additionalShippingFeePerTimes,
  price,
  customOptions,
}: {
  name: string;
  name_en?: string;
  startAt: string;
  endAt: string;
  status: string;
  benefit: Array<LotteryBenefit>;
  isGlobal?: boolean;
  isCampaign?: boolean;
  showSpKv?: boolean;
  estimatedShippingDate?: string | null;
  additionalShippingFeePerTimes?: number;
  price?: LotteryPrice;
  customOptions?: LotteryCustomOption;
}) {
  const { t, i18n } = useTranslation();
  const isShowEstimatedShippingDate = () => {
    if (customOptions?.hideEstimatedShipmentDays === '1') {
      return false;
    }

    // シリアルコードでは一旦非表示
    return !isCampaign && showEstimatedShippingDate(endAt, estimatedShippingDate);
  };
  return (
    <header className={styles.componentStyle}>
      {/* /開催中、開催予定等の表示ラベル部分の制御 */}
      {showSpKv && (
        <div className={styles.itemStatus}>
          <img src={eventStatusLabel(status)} alt='' />
        </div>
      )}

      <p className={`${styles.itemTitle} ${utilsStyles.textBold}`}>
        {getLocalizedText(i18n.resolvedLanguage, { ja: name, en: name_en ?? '' })}
      </p>
      <div className={styles.labelArea}>
        {isCampaign ? (
          <div className={styles.campaignLabel}>
            <span className={utilsStyles.textBold}>{t('campaignLottery')}</span>
          </div>
        ) : (
          <div className={styles.benefitList}>
            {benefit.length > 0 && (
              <p className={`${styles.benefitListItem} ${utilsStyles.textBold}`}>{t('benefitsAvailable')}</p>
            )}
          </div>
        )}
      </div>
      <div className={`${styles.lotteryDate} ${utilsStyles.textBold}`}>
        <FaIcon icon={faClock} />
        <p>
          {formatDateCommonI18n(startAt, i18n.resolvedLanguage)}&nbsp;~&nbsp;
          {formatDateCommonI18n(endAt, i18n.resolvedLanguage)}
        </p>
      </div>
      {/* {showEstimatedShippingDate(endAt, estimatedShippingDate) && ( */}
      {isShowEstimatedShippingDate() && (
        <div className={`${styles.estimateShippingDate} ${utilsStyles.textBold}`}>
          <FaIcon icon={faTruck} />
          <p>
            {estimatedShippingDateTextI18n(t, {
              eventEndAt: endAt,
              estimatedShippingDate: estimatedShippingDate || undefined,
            })}
          </p>
        </div>
      )}
      {drawFeeAnnotation(price, t, i18n.resolvedLanguage)}
      {shippingAlert(price, additionalShippingFeePerTimes)}
    </header>
  );
}

function drawFeeAnnotation(
  price?: LotteryPrice,
  t?: (key: string, options?: Record<string, string | number>) => string,
  lang?: string,
) {
  if (!price || !t) return <></>;
  if (price.drawFee === 0) return <></>;
  return (
    <div className={`${utilsStyles.flexRowNowrap} ${utilsStyles.alignCenter} ${styles.feeTextContainer}`}>
      <p className={styles.drawFeeText}>{t('drawFeeText', { drawFee: formatCurrency(price.drawFee) })}</p>
      {isJapanese(lang) && (
        <p className={utilsStyles.textXSmall}>
          /&nbsp;
          {hasShippingFee(price.shippingFee, price.additionalShippingFee)
            ? '（配送手数料を含まない）'
            : '（配送手数料を含む）'}
        </p>
      )}
    </div>
  );
}

function shippingAlert(price?: LotteryPrice, additionalShippingFeePerTimes?: number) {
  if (!price) return <></>;
  if (price.shippingFee === 0) return <></>;
  if (!hasShippingFee(price.shippingFee, price.additionalShippingFee)) return <></>;
  return (
    <div className={styles.feeTextContainer}>
      <ShippingAlert price={price} additionalShippingFeePerTimes={additionalShippingFeePerTimes} />
    </div>
  );
}

export default LotteryDetailHeader;
